import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const ExecutionHistory = () => {
  useAuth(); // Verify authentication
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [executionHistory, setExecutionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchExecutionHistory();
  }, []);

  const fetchExecutionHistory = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/search-execution/batches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExecutionHistory(data.batches || []);
      } else {
        throw new Error('Failed to fetch execution history');
      }
    } catch (error) {
      console.error('Failed to fetch execution history:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    try {
      setDeleting(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      const response = await fetch(`${apiUrl}/search-execution/batches/${batchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setExecutionHistory(prev => prev.filter(batch => batch.id !== batchId));
        setDeleteConfirm(null);
        console.log(result.message);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete batch');
      }
    } catch (error) {
      console.error('Failed to delete batch:', error);
      setError(`Failed to delete batch: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] dashboard-optimized">
        <div className="glass-spinner w-12 h-12 mb-4"></div>
        <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading execution history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="glass-alert-error rounded-lg p-6 max-w-md w-full">
          <div className="text-red-300 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="heading-mono text-base text-red-300 mb-2 text-center">Failed to load history</h3>
          <p className="text-mono text-sm text-red-200 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-scale">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`heading-mono text-2xl font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Execution History
          </h1>
          <p className={`text-mono text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            View and manage your search analysis history
          </p>
        </div>
        <span className="glass-badge text-mono text-sm px-4 py-2">
          {executionHistory.length} batch{executionHistory.length !== 1 ? 'es' : ''} executed
        </span>
      </div>

      {/* Execution History List */}
      {executionHistory.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <div className="glass-dark rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className={`heading-mono text-base mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>No executions yet</h3>
          <p className={`text-mono text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Execute search templates to see analysis results here.
          </p>
          <button
            onClick={() => navigate('/search')}
            className="btn-glass-accent px-6 py-3 rounded-lg text-mono font-medium hover:scale-105 transform transition-all"
          >
            Go to Search
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {executionHistory.map((batch) => (
            <div key={batch.id} className="glass-card rounded-xl p-6 dashboard-optimized hover:scale-[1.01] transition-transform">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h4 className={`text-mono text-base font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {batch.name}
                    </h4>
                  </div>
                  {formatDate(batch.createdAt) && (
                    <p className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(batch.createdAt)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {batch.status === 'completed' && (
                    <button
                      onClick={() => navigate(`/results/${batch.id}`)}
                      className="btn-glass-accent text-mono text-sm font-medium px-4 py-2 rounded-lg hover:scale-105 transform transition-all"
                    >
                      View Results
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm({ batchId: batch.id, batchName: batch.name })}
                    disabled={batch.status === 'running'}
                    className="btn-glass text-red-400 hover:text-red-300 text-mono text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transform transition-all"
                    title={batch.status === 'running' ? 'Cannot delete running batch' : 'Delete this analysis'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog - Rendered via Portal */}
      {deleteConfirm && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="glass-modal rounded-2xl p-8 max-w-md w-full animate-fade-in-scale">
            <h3 className={`heading-mono text-lg text-glow mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Delete Analysis
            </h3>
            <p className={`text-mono text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to delete the analysis "<strong className={isDark ? 'text-white' : 'text-gray-800'}>{deleteConfirm.batchName}</strong>"? 
              This will permanently remove all execution results, competitor data, and individual records. 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="btn-glass px-6 py-3 rounded-lg text-mono font-medium disabled:opacity-50 hover:scale-105 transform transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBatch(deleteConfirm.batchId)}
                disabled={deleting}
                className="glass-badge-error px-6 py-3 rounded-lg text-mono font-medium disabled:opacity-50 hover:scale-105 transform transition-all"
              >
                {deleting ? (
                  <div className="flex items-center gap-2">
                    <div className="glass-spinner w-4 h-4"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExecutionHistory;